import Map from "@components/Mapa/Map";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { useTimezone } from "@contexts/useTimezone";
import { PresenteeismFailureAttemptsServiceModel } from "@services/PresenteeismFailureAttemptsService";
import { HollowedButton } from "@shared/designSystem/Button";
import { ColumnsContainer, HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import DetailSection from "@shared/designSystem/DetailSection";
import { Modal } from "@shared/designSystem/messages/Modal";
import UserCard from "@shared/designSystem/UserCard";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { Description } from "@shared/utils/DescriptionsHelper";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import {
    getPresenteeismFailureMessageLabel,
    getPresenteeismFailureReasonLabel,
    hasSecondFactorFailureMetadata,
    isSecondFactorFailureAttempt,
} from "./PresenteeismFailureAttemptPresentation";

interface PresenteeismFailureAttemptDetailsModalProps {
    attempt: PresenteeismFailureAttemptsServiceModel.PresenteeismFailureAttempt | null;
    isOpen: boolean;
    onClose: () => void;
}

const earthRadiusInMeters = 6371000;

const toRadians = (value: number) => {
    return (value * Math.PI) / 180;
};

const computeDistanceInMeters = (
    from: { latitude: number; longitude: number },
    to: { latitude: number; longitude: number },
) => {
    const latitudeDelta = toRadians(to.latitude - from.latitude);
    const longitudeDelta = toRadians(to.longitude - from.longitude);
    const fromLatitude = toRadians(from.latitude);
    const toLatitude = toRadians(to.latitude);

    const a = Math.sin(latitudeDelta / 2) ** 2
        + Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadiusInMeters * c;
};

const buildGoogleMapsCoordinatesURL = (latitude: number, longitude: number) => {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

const buildGoogleMapsDirectionsURL = (
    origin: { latitude: number; longitude: number },
    destination: { latitude: number; longitude: number },
) => {
    return `https://www.google.com/maps/dir/?api=1&origin=${origin.latitude},${origin.longitude}&destination=${destination.latitude},${destination.longitude}`;
};

const stringifyMetadataValue = (value: unknown): string | null => {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    return JSON.stringify(value);
};

export const PresenteeismFailureAttemptDetailsModal: React.FC<PresenteeismFailureAttemptDetailsModalProps> = ({
    attempt,
    isOpen,
    onClose,
}) => {
    const { timezone } = useTimezone();

    const attemptLocation = attempt?.location;
    const mainQontactPointBuilding = attempt?.closestQontactPoint?.building;

    const mainBuildingCoordinates = mainQontactPointBuilding?.latitude != null
        && mainQontactPointBuilding?.longitude != null
        ? {
            latitude: mainQontactPointBuilding.latitude,
            longitude: mainQontactPointBuilding.longitude,
        }
        : null;

    const computedDistanceInMeters = attemptLocation && mainBuildingCoordinates
        ? computeDistanceInMeters(attemptLocation, mainBuildingCoordinates)
        : null;

    const triggerDistanceInMeters = attempt?.closestQontactPoint?.qontactPoint?.triggerDistance ?? null;

    const mapMarkers = attemptLocation && mainBuildingCoordinates
        ? [
            {
                id: "attempt-location",
                status: "RED",
                latitude: attemptLocation.latitude,
                longitude: attemptLocation.longitude,
                title: "Ubicación recibida".localized(),
            },
            {
                id: "qontact-point-building",
                status: "GREEN",
                latitude: mainBuildingCoordinates.latitude,
                longitude: mainBuildingCoordinates.longitude,
                title: mainQontactPointBuilding?.name ?? "Qontact Point principal".localized(),
            },
        ]
        : [];

    const mapCircles = mainBuildingCoordinates && triggerDistanceInMeters != null
        ? [
            {
                id: "qontact-point-influence-radius",
                center: {
                    latitude: mainBuildingCoordinates.latitude,
                    longitude: mainBuildingCoordinates.longitude,
                },
                radiusInMeters: triggerDistanceInMeters,
                strokeColor: "#EC6C56",
                fillColor: "#EC6C56",
            },
        ]
        : [];

    const attemptLocationURL = attemptLocation
        ? buildGoogleMapsCoordinatesURL(attemptLocation.latitude, attemptLocation.longitude)
        : null;
    const mainBuildingCoordinatesURL = mainBuildingCoordinates
        ? buildGoogleMapsCoordinatesURL(mainBuildingCoordinates.latitude, mainBuildingCoordinates.longitude)
        : null;
    const directionsURL = attemptLocation && mainBuildingCoordinates
        ? buildGoogleMapsDirectionsURL(attemptLocation, mainBuildingCoordinates)
        : null;
    const isSecondFactorAttempt = attempt ? isSecondFactorFailureAttempt(attempt) : false;
    const metadata = attempt?.metadata;
    const analyticsMetadata = metadata?.analytics;
    const shouldShowSecondFactorSection = isSecondFactorAttempt && hasSecondFactorFailureMetadata(metadata);
    const hasCoordinateContext = Boolean(attemptLocation || mainBuildingCoordinates || attempt?.closestQontactPoint);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={attempt ? getPresenteeismFailureReasonLabel(attempt.failureReason) : "Detalle".localized()}
            width="880px"
            height="85vh"
            maxWidth="94vw"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                {attempt ? (
                    <>
                        <UserCard
                            name={attempt.user?.name ?? attempt.requesterID}
                            imageURL={attempt.user?.imageURL}
                            width="100%"
                        />

                        <div style={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                            <VerticalContainer
                                width="100%"
                                gap={designSystemStyles.gapLg}
                                paddingBottom={designSystemStyles.paddingSm}
                            >
                                <DetailSection title={"Resumen".localized()}>
                                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                        <DetailField label={"Tipo".localized()} value={attempt.type} />
                                        <DetailField
                                            label={"Fecha".localized()}
                                            value={Description.Timestamp.date(attempt.timestamp, timezone)}
                                        />
                                        <DetailField label={"Motivo".localized()} value={getPresenteeismFailureReasonLabel(attempt.failureReason)} />
                                        <DetailField label={"Solicitante".localized()} value={attempt.requesterID} />
                                        <ClickableWrapper onClick={() => {
                                            if (attempt.ticketID) {
                                                window.open("https://tickets.qontact.com.ar/tickets/" + attempt.ticketID, "_blank");
                                            }
                                        }}>
                                            <DetailField label={"Ticket".localized()} value={attempt.ticketID} />
                                        </ClickableWrapper>
                                        <DetailField label={"Día".localized()} value={attempt.day} />
                                    </ColumnsContainer>
                                    <DetailField label={"Mensaje".localized()} value={getPresenteeismFailureMessageLabel(attempt.errorMessage)} />
                                </DetailSection>

                                {shouldShowSecondFactorSection ? (
                                    <DetailSection title={"2FA".localized()}>
                                        <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                            <DetailField label={"Sesión".localized()} value={metadata?.sessionID} />
                                            <DetailField label={"Challenge".localized()} value={metadata?.challengeID} />
                                            <DetailField label={"Problema de permisos".localized()} value={metadata?.problemType?.localized()} />
                                            <DetailField label={"Origen".localized()} value={metadata?.source} />
                                            <DetailField label={"Instalación".localized()} value={metadata?.deviceInstallationID} />
                                            <DetailField label={"Teléfono reportado".localized()} value={metadata?.offenderPhoneNumber} />
                                            <DetailField label={"Plataforma detectada".localized()} value={metadata?.detectedDevicePlatform} />
                                            <DetailField label={"Plataforma asociada".localized()} value={metadata?.boundDevicePlatform} />
                                            <DetailField label={"Acción detectada".localized()} value={metadata?.detectedDeviceNextAction} />
                                            <DetailField label={"Método intentado".localized()} value={metadata?.completionMethodAttempted} />
                                            <DetailField label={"Control".localized()} value={analyticsMetadata?.controlName} />
                                            <DetailField label={"Resultado del control".localized()} value={analyticsMetadata?.controlOutcome} />
                                            <DetailField label={"Motivo del control".localized()} value={analyticsMetadata?.controlReason?.localized()} />
                                            <DetailField label={"Plataforma de solicitud".localized()} value={analyticsMetadata?.requestPlatform} />
                                            <DetailField label={"Permisos reportados".localized()} value={stringifyMetadataValue(metadata?.rawPermissionState)} />
                                            <DetailField label={"Detalles".localized()} value={stringifyMetadataValue(metadata?.problemDetails)} />
                                        </ColumnsContainer>
                                    </DetailSection>
                                ) : null}

                                {hasCoordinateContext ? (
                                    <DetailSection title={"Coordenadas".localized()}>
                                        <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                            <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                                                <SmallText text={"Ubicación recibida".localized()} color={designSystemStyles.colorText} />
                                                {attemptLocation && attemptLocationURL ? (
                                                    <a
                                                        href={attemptLocationURL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ textDecoration: "none" }}
                                                    >
                                                        <Text
                                                            text={`${attemptLocation.latitude}, ${attemptLocation.longitude}`}
                                                            color={designSystemStyles.colorMainAction}
                                                        />
                                                    </a>
                                                ) : (
                                                    <Text text={"N/D".localized()} />
                                                )}
                                            </VerticalContainer>
                                            <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                                                <SmallText text={"Edificio de referencia".localized()} color={designSystemStyles.colorText} />
                                                {mainBuildingCoordinates && mainBuildingCoordinatesURL ? (
                                                    <a
                                                        href={mainBuildingCoordinatesURL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ textDecoration: "none" }}
                                                    >
                                                        <Text
                                                            text={`${mainBuildingCoordinates.latitude}, ${mainBuildingCoordinates.longitude}`}
                                                            color={designSystemStyles.colorMainAction}
                                                        />
                                                    </a>
                                                ) : (
                                                    <Text text={"N/D".localized()} />
                                                )}
                                            </VerticalContainer>
                                            <DetailField
                                                label={"Distancia calculada".localized()}
                                                value={computedDistanceInMeters != null ? `${computedDistanceInMeters.toFixed(2)} m` : null}
                                            />
                                            <DetailField
                                                label={"Qontact Point más cercano".localized()}
                                                value={attempt.closestQontactPoint?.buildingName ?? mainQontactPointBuilding?.name ?? null}
                                            />
                                            <DetailField
                                                label={"Distancia al Qontact Point más cercano".localized()}
                                                value={attempt.closestQontactPoint?.distanceInMeters != null
                                                    ? `${attempt.closestQontactPoint.distanceInMeters.toFixed(2)} m`
                                                    : null}
                                            />
                                            <DetailField
                                                label={"Radio permitido".localized()}
                                                value={triggerDistanceInMeters != null
                                                    ? `${triggerDistanceInMeters.toFixed(2)} m`
                                                    : null}
                                            />
                                        </ColumnsContainer>
                                        <SmallText
                                            text={"La referencia se calcula contra las coordenadas del edificio asociado al Qontact Point más cercano.".localized()}
                                            color={designSystemStyles.colorTextSecondary}
                                        />
                                        {directionsURL ? (
                                            <a
                                                href={directionsURL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: "none" }}
                                            >
                                                <Text
                                                    text={"Abrir ruta en Google Maps".localized()}
                                                    color={designSystemStyles.colorMainAction}
                                                />
                                            </a>
                                        ) : null}
                                    </DetailSection>
                                ) : null}

                                {hasCoordinateContext ? (
                                    <DetailSection title={"Mapa".localized()}>
                                        <div style={{ width: "100%", height: "320px" }}>
                                            {mapMarkers.length === 2 ? (
                                                <Map
                                                    markers={mapMarkers}
                                                    circles={mapCircles}
                                                    radius={designSystemStyles.cornerRadiusComponent}
                                                    setMapRefParent={() => {}}
                                                />
                                            ) : (
                                                <QontyEmptyState
                                                    text={"No hay coordenadas suficientes para mostrar el mapa.".localized()}
                                                    fullSize={false}
                                                />
                                            )}
                                        </div>
                                    </DetailSection>
                                ) : null}
                            </VerticalContainer>
                        </div>

                        <HorizontalContainer justifyContent="flex-end" width="100%">
                            <HollowedButton
                                text={"Cerrar".localized()}
                                onClick={onClose}
                            />
                        </HorizontalContainer>
                    </>
                ) : null}
            </VerticalContainer>
        </Modal>
    );
};

export default PresenteeismFailureAttemptDetailsModal;
